import * as React from 'react';
import { ButtonAsWrapper, FlexRow, TextDynamic, TypographyProps } from '@minecraft.atoms';
import Icon, { IconProps } from '@minecraft.icon';
import { BaseDropdownButton } from './BaseDropdownButton';
import { DropdownItemProps, OptionalProps } from './types';

type TextButtonProps = {
  /** text used as the toggle of the button */
  text: string;
  /** size for the text in the toggle */
  fontSize?: TypographyProps['fontSize'];
  /** weight for the text in the toggle */
  fontWeight?: TypographyProps['fontWeight'];
  /** label for the toggle button wrapper so that screen readers can describe the action */
  label: string;
  /** size of the toggle chevron icon */
  iconSize?: IconProps['size'];
  /** pass a ref down to the underlying button element
   * this is used so that the DropdownToggle
   * correctly opens & positions the dropdown menu
   * do not rename this prop!!
   */
  innerRef?: React.Ref<HTMLButtonElement>;
  /** this is passed through from the implementation in BaseDropdownButton
   * you should not need to use this prop manually, but we do need to declare it here
   * so that we can intercept the onClick event and stop it propagating
   * (otherwise clicking the meatball toggle will also trigger any onClick in parent element(s))
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const TextButton = ({
  text,
  fontSize = 'h2',
  fontWeight = 'semibold',
  iconSize = 'smm',
  innerRef,
  onClick,
  label,
  ...props
}: TextButtonProps) => {
  const iconName = props?.['aria-expanded'] ? 'chevron-open-thin' : 'chevron-closed-thin';

  return (
    <ButtonAsWrapper
      aria-label={label}
      ref={innerRef}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(e);
      }}
      {...props}
    >
      <FlexRow gap="2" alignItems="center">
        <TextDynamic fontSize={fontSize} fontWeight={fontWeight}>
          {text}
        </TextDynamic>
        <Icon name={iconName} size={iconSize} />
      </FlexRow>
    </ButtonAsWrapper>
  );
};

export interface TextDropdownButtonProps extends OptionalProps {
  block?: boolean;
  menuAlign?: 'right' | 'left';
  menuItemsProps: DropdownItemProps[];
  mobileBlock?: boolean;
  toggleProps: Omit<TextButtonProps, 'onClick'>;
}

export const TextDropdownButton = ({
  toggleProps,
  menuAlign = 'right',
  menuItemsProps = [],
  block = false,
  mobileBlock = false,
  'data-testid': dataTestId,
  qaId,
  className,
}: TextDropdownButtonProps) => (
  <BaseDropdownButton
    block={block}
    className={className}
    data-testid={dataTestId}
    menuAlign={menuAlign}
    menuItemsProps={menuItemsProps}
    mobileBlock={mobileBlock}
    qaId={qaId}
    toggleChildren={undefined}
    ToggleComponent={TextButton}
    toggleProps={{ ...toggleProps }}
  />
);
